import { Injectable, OnDestroy, NgZone, Optional } from '@angular/core';
import { Router, ActivationEnd } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Subscription, filter } from 'rxjs';
import { stringify } from 'flatted';

import { BUILD_NUMBER, ENVIRONMENT } from '../../config';
import { User } from '../models/user';

@Injectable({ providedIn: 'root' })
export class AnalyticsService implements OnDestroy {
	private user: User | undefined;
	private readonly sub?: Subscription;

	private currentServiceList: Set<string>;
	private pageName: string;

	constructor(
		@Optional() private readonly appInsights: ApplicationInsights | null,
		readonly router: Router,
		readonly ngZone: NgZone,
	) {
		this.currentServiceList = new Set<string>();
		this.pageName = '';
		this.sub = this.router.events
			.pipe(
				filter((ev): ev is ActivationEnd => ev instanceof ActivationEnd && !ev.snapshot.firstChild),
			)
			.subscribe(ev => {
				this.currentServiceList =
				new Set(
					ev.snapshot.pathFromRoot
						.filter(path => Boolean(path.data['service']))
						.map(path => String(path.data['service']))
				);
				this.pageName = ev.snapshot.data['pageName'] ? String(ev.snapshot.data['pageName']) : '';
			});
	}

	ngOnDestroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}

	setUserContext(user?: User) {
		this.user = user;
		this.updateContextAnalytics();
	}

	updateContextAnalytics() {
		this.ngZone.runOutsideAngular(() => {
			if (!this.appInsights) {
				return;
			}
			if (this.user) {
				this.appInsights.setAuthenticatedUserContext(String(this.user.currentVisibility.organizationId));
			} else {
				this.appInsights.clearAuthenticatedUserContext();
			}
		});
	}

	getProperties() {
		return {
			internalUser: this.user ? String(this.user.isSimulatingOrDelegating()) : 'NA',
			supplierId: this.user ? String(this.user.currentVisibility.supplierId) : 'NA',
			platform: 'portal', // portal version
			env: ENVIRONMENT,
			resolution: `${ screen.width } x ${ screen.height }`,
			pixelRatio: String(devicePixelRatio),
			version: BUILD_NUMBER,
			service: Array.from(this.currentServiceList).join(',')
		};
	}

	trackEvent(eventName: string) {
		this.updateContextAnalytics();
		this.ngZone.runOutsideAngular(() => {
			if (!this.appInsights) {
				return;
			}
			this.appInsights.trackEvent({ name: eventName, properties: this.getProperties() });
		});
	}

	trackPage(pageUrl: string) {
		this.pageName = this.pageName === '' ? pageUrl : this.pageName;
		this.ngZone.runOutsideAngular(() => {
			if (!this.appInsights) {
				return;
			}
			this.appInsights.trackPageView({ name: this.pageName, uri: pageUrl, properties: this.getProperties() });
		});
	}

	trackException(err: unknown) {
		this.ngZone.runOutsideAngular(() => {
			if (!this.appInsights) {
				return;
			}
			this.appInsights.trackException(err instanceof Error
				? {
					error: err,
					severityLevel: 3,
					properties: this.getProperties()
				}
				: {
					error: new Error(stringify(err)),
					severityLevel: 3,
					properties: this.getProperties()
				});
		});
	}
}
